import type React from "react";
import { useMemo } from "react";

import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { $robotoFontFamily, colorsPalettes } from "@similarweb/styles";
import { FlexRow } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { DeleteKeywordListConfirmationModal } from "app/components/modals/components/DeleteKeywordListConfirmationModal";
import { useKeywordGroupModalWorkflow } from "app/pages/keyword-analysis/useKeywordGroupModalWorkflow";
import type { IUserKeywordGroup, KeywordOrGroupType } from "app/services/keyword-groups";
import { AutocompleteDemandAnalysisUnified } from "pro-features/components/AutocompleteDemandAnalysisUnified/AutocompleteDemandAnalysisUnified";
import { AutocompleteKeywordGroups } from "pro-features/components/AutocompleteKeywords/AutocompleteKeywordsGroups";
import type { IAiTopic } from "pro-features/components/AutocompleteKeywords/types/AutocompleteKeywordGroupTypes";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { allTrackers } from "app/services/track/track";

export type KeywordListComponentMode = "keywordLists" | "full";

type KeywordListClickHandlerGenerator = (
    item: IAiTopic | KeywordOrGroupType | KeywordOrGroupType[] | Partial<KeywordOrGroupType> | string | string[],
) => VoidFunction;

const PlaceholderText = styled(FlexRow)`
    color: ${colorsPalettes.carbon["300"]};
    font-family: ${$robotoFontFamily};
    font-size: 14px;
    margin-left: 32px;
    gap: 4px;

    svg {
        width: 16px;
        height: 16px;
    }

    svg path {
        fill: ${colorsPalettes.carbon["200"]};
    }
`;

const BoldPlaceholderText = styled.span`
    font-weight: 500;
`;

export interface IDemandAnalysisHomeSearchProps {
    mode: KeywordListComponentMode;
    countryFilter?: React.ReactElement;
    supportCountryFilters: boolean;
    handleItemClick: KeywordListClickHandlerGenerator;
    showAutoCreateKeywordGroupButton?: boolean;
    handleSave: (modifiedGroup: IUserKeywordGroup) => void;
    handleKeywordGroupButtonClick?: KeywordListClickHandlerGenerator;
    textContent: {
        placeholderText?: string;
        placeholderBoldText?: string;
        keywordsTabTitle?: string;
        keywordGroupsTabTitle?: string;
        topicsTabTitle?: string;
        aiTopicsTabTitle?: string;
    };
}
export const DemandAnalysisHomeSearch = ({
    mode,
    textContent,
    countryFilter,
    supportCountryFilters,
    showAutoCreateKeywordGroupButton,
    handleSave,
    handleItemClick,
    handleKeywordGroupButtonClick,
}: IDemandAnalysisHomeSearchProps) => {
    const t = useTranslation();

    const { controller, requestDelete, deleteConfirmationModalProps } = useKeywordGroupModalWorkflow({
        onDeleteSuccess: () => {
            allTrackers.dispatchTrackEvent("Pop Up", "click", "Create New Keyword Group/Delete");
        },
    });

    const handleBeforeDeleteKeywordGroup = (group: IUserKeywordGroup | undefined) => {
        controller.hide();

        if (!group) {
            return;
        }

        requestDelete(group);
    };

    const placeholder = useMemo(
        () => (
            <PlaceholderText>
                {textContent.placeholderText ? t(textContent.placeholderText) : ""}{" "}
                <BoldPlaceholderText>
                    {textContent.placeholderBoldText ? t(textContent.placeholderBoldText) : ""}
                </BoldPlaceholderText>
                {mode === "keywordLists" && supportCountryFilters ? (
                    <SWReactIcons
                        iconName="wizard"
                        size="xs"
                    />
                ) : null}
            </PlaceholderText>
        ),
        [textContent, supportCountryFilters],
    );

    return (
        <>
            <Flex
                gap="8px"
                alignItems="center"
                justifyContent="center"
                position="relative"
            >
                {countryFilter}
                <Flex flexGrow="1">
                    {mode === "full" ? (
                        <AutocompleteDemandAnalysisUnified
                            showGoToMonitorKeywordListButton
                            shouldConfirmDeleteKeywordList
                            isPrimaryEntitySelection
                            autocompleteProps={{ placeholder }}
                            handleSave={handleSave}
                        />
                    ) : mode === "keywordLists" ? (
                        <AutocompleteKeywordGroups
                            controller={controller}
                            onClick={(item) => handleItemClick(item)()}
                            onAutoCreateKeywordGroupClick={
                                handleKeywordGroupButtonClick
                                    ? (item) => handleKeywordGroupButtonClick(item)()
                                    : undefined
                            }
                            autocompleteProps={{ placeholder }}
                            handleSave={handleSave}
                            showGoToMonitorKeywordListButton
                            onBeforeDeleteKeywordGroup={handleBeforeDeleteKeywordGroup}
                            tabTitles={textContent}
                            showAutoCreateKeywordGroupButton={showAutoCreateKeywordGroupButton}
                        />
                    ) : null}
                </Flex>
            </Flex>
            <DeleteKeywordListConfirmationModal
                {...deleteConfirmationModalProps}
                dataAutomationPrefix="demand-analysis-home-search-delete-keyword-list-confirmation"
            />
        </>
    );
};
